import api, { baseURL } from "../../../utils/api";
import {
  getCachedHistory,
  regroupThreadsByDate,
  setCachedHistory,
} from "../../../components/SideBar/cache/caching";
import { isDraftThreadId, getDraftBotId } from "./addThreadActions";

export const useSendMessage = (
  token,
  user_level,
  thread,
  threadId,
  botRole,
  setMessages,
  setIsLoading,
  setHasError,
  setWait,
  setDidSearch,
  setLinkSearch,
  setData,
  setNewMessage,
  runId,
  setRunId,
  messageId,
  setMessageId,
  setEventError,
  setRefresh,
  setHistory,
  eventSourceRef,
  setThreadId,
  suppressNextThreadFetchRef,
) => {
  const promoteDraftThread = async (draftId) => {
    const botId = getDraftBotId(draftId) || 1;
    const response = await api.post(
      "/v2/responses-chat/conversation",
      { bot_id: botId, title: "New Chat" },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (!response?.data?.id) {
      throw new Error("Invalid response from server");
    }

    const realThreadId = response.data.id;

    setHistory((prevHistory) => {
      const allItems = Object.values(prevHistory || {})
        .flat()
        .filter(Boolean);
      const filtered = allItems.filter(
        (item) => item.thread_id !== draftId && item.thread_id !== realThreadId,
      );
      const timestamp = new Date().toISOString();
      const newItem = {
        id: realThreadId,
        thread_id: realThreadId,
        name: response.data.title || "New Chat",
        updated_at: response.data.updated_at || timestamp,
        last_used: response.data.updated_at || timestamp,
        thread: null,
        bot_role: "responses_api",
        bot_id: response.data.bot_id || botId,
      };
      const regrouped = regroupThreadsByDate([newItem, ...filtered]);
      setCachedHistory(regrouped);
      return regrouped;
    });

    // The message is about to stream into this same thread, so the
    // history-fetch effect that normally fires on thread switches should
    // sit this one out — otherwise it clobbers the in-flight optimistic
    // messages with whatever the server had saved a split second earlier.
    if (suppressNextThreadFetchRef) {
      suppressNextThreadFetchRef.current = true;
    }
    setThreadId?.(realThreadId);
    localStorage.setItem("threadId", realThreadId);
    sessionStorage.setItem("threadId", realThreadId);

    return realThreadId;
  };
  const refreshThreadHistoryOrder = (
    targetThreadId,
    updatedAt = new Date().toISOString(),
  ) => {
    const updateItems = (historyData) => {
      if (!historyData) return null;

      const allItems = Object.values(historyData).flat();
      const updatedItems = allItems.map((item) => {
        if (item.thread_id?.toString() === targetThreadId?.toString()) {
          return {
            ...item,
            updated_at: updatedAt,
            last_used: updatedAt,
          };
        }

        return item;
      });

      return regroupThreadsByDate(updatedItems);
    };

    if (setHistory) {
      setHistory((currentHistory) => {
        const regroupedHistory = updateItems(currentHistory);
        if (regroupedHistory) setCachedHistory(regroupedHistory);
        return regroupedHistory || currentHistory;
      });
      return;
    }

    const cached = getCachedHistory();
    const regroupedHistory = updateItems(cached);
    if (regroupedHistory) setCachedHistory(regroupedHistory);
  };
  const findLastAssistantMessageIndex = (messages = []) => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index]?.role === "assistant") return index;
    }

    return -1;
  };

  const updateMessageErrorState = (threadId, errorText) => {
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const assistantIndex = findLastAssistantMessageIndex(updatedMessages);

      if (assistantIndex === -1) {
        updatedMessages.push({
          role: "assistant",
          text_value: errorText,
          isAnalyzing: false,
          isStreamError: true,
          timestamp: new Date().toISOString(),
        });
      } else {
        updatedMessages[assistantIndex] = {
          ...updatedMessages[assistantIndex],
          role: "assistant",
          text_value: errorText,
          isAnalyzing: false,
          isStreamError: true,
        };
      }

      if (!isDraftThreadId(threadId)) {
        localStorage.setItem(
          `messages_${threadId}`,
          JSON.stringify(updatedMessages),
        );
      }
      return updatedMessages;
    });
  };

  const getStreamErrorMessage = (selectedBotRole, reason = "error") => {
    if (reason === "token_limit") {
      return "You've reached the token limit for your current plan. Please start a new chat or upgrade your plan to continue this conversation.";
    }

    if (reason === "empty") {
      return selectedBotRole === "electric_coop"
        ? "The Electric Coop chat did not return a response. Please try again in a moment."
        : "BETH did not return a response. Please try again in a moment.";
    }

    return selectedBotRole === "electric_coop"
      ? "The Electric Coop chat could not finish the response. Please check your connection and try again."
      : "BETH could not finish the response. Please check your connection and try again.";
  };

  const readErrorBody = async (response) => {
    try {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      return "";
    }
  };

  const getErrorText = (errorPayload) => {
    if (!errorPayload) return "";
    if (typeof errorPayload === "string") return errorPayload;
    return (
      errorPayload.error ||
      errorPayload.detail ||
      errorPayload.message ||
      JSON.stringify(errorPayload)
    );
  };

  const isTokenLimitError = (response, errorPayload) => {
    const errorText = getErrorText(errorPayload).toLowerCase();
    return (
      response?.status === 402 ||
      response?.status === 429 ||
      errorText.includes("token") ||
      errorText.includes("quota") ||
      errorText.includes("limit") ||
      errorText.includes("free account")
    );
  };

  const createStreamError = (message, reason = "error") => {
    const error = new Error(message || "Responses API message stream failed");
    error.streamFailureReason = reason;
    return error;
  };

  const parseStreamErrorPayload = (data) => {
    try {
      const parsed = JSON.parse(data);
      if (
        parsed &&
        typeof parsed === "object" &&
        (parsed.error ||
          parsed.detail ||
          parsed.message ||
          parsed.event === "error")
      ) {
        return parsed;
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const handleStreamFailure = (threadId, selectedBotRole, reason = "error") => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    updateMessageErrorState(
      threadId,
      getStreamErrorMessage(selectedBotRole, reason),
    );
    setIsLoading(false);
    setEventError(false);
    setWait(false);
    setDidSearch(false);
    setLinkSearch(false);
    setHasError(false);
  };

  const updatePromptTokenCount = async (tokenCount) => {
    if (user_level !== "free" || !tokenCount) return;

    await api.post(
      `/chat/update/token?chat_token=${tokenCount}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  };
  const handleSendMessage = async (
    message,
    file,
    sender = "user",
    tokenCount,
  ) => {
    const trimmedMessage = message.trim();
    setNewMessage("");
    setEventError(false);
    const selectedFiles = file ? Array.from(file) : [];

    const imageTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
    const docTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "text/plain",
    ];

    const imageFiles = selectedFiles.filter((f) => imageTypes.includes(f.type));
    const documentFiles = selectedFiles.filter((f) =>
      docTypes.includes(f.type),
    );

    setIsLoading(true);
    setLinkSearch(false);

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        role: sender,
        text_value: trimmedMessage,
        file: documentFiles.map((f) => f.name),
        image: imageFiles.map((f) => ({
          name: f.name,
          url: URL.createObjectURL(f),
        })),
        timestamp: new Date().toISOString(),
      },
      {
        role: "assistant",
        text_value: "Analyzing your message...",
        isAnalyzing: true,
        timestamp: new Date().toISOString(),
      },
    ]);

    let activeThreadId = threadId;

    try {
      if (isDraftThreadId(activeThreadId)) {
        activeThreadId = await promoteDraftThread(activeThreadId);
      }

      const formData = new FormData();
      if (documentFiles.length > 0) {
        documentFiles.forEach((f) => formData.append("files", f));
      }
      if (imageFiles.length > 0) {
        imageFiles.forEach((f) => formData.append("images", f));
      }
      formData.append("thread", thread);
      formData.append("thread_id", activeThreadId);
      formData.append("message", trimmedMessage);

      await handleResponsesApiMessage(
        activeThreadId,
        formData,
        trimmedMessage,
        tokenCount,
      );
    } catch (error) {
      handleStreamFailure(
        activeThreadId,
        "responses_api",
        error.streamFailureReason || "error",
      );
    }
  };
  const appendStreamText = (threadId, chunk, accumulatedTextRef) => {
    accumulatedTextRef.value += chunk;
    setDidSearch(false);
    setIsLoading(false);
    setWait(true);
    updateMessageState(threadId, accumulatedTextRef.value);
  };

  const updateLatestMessageIds = (threadId, assistantMessageId) => {
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const assistantIndex = findLastAssistantMessageIndex(updatedMessages);

      if (assistantIndex === -1) return prevMessages;

      updatedMessages[assistantIndex] = {
        ...updatedMessages[assistantIndex],
        id: assistantMessageId,
      };

      // The backend resolves a message's prompt/answer pair from either
      // id, so backfilling the same id on the paired user prompt lets
      // "download this message" work from either bubble.
      const userIndex = assistantIndex - 1;
      if (
        userIndex >= 0 &&
        updatedMessages[userIndex]?.role === "user" &&
        updatedMessages[userIndex]?.id == null
      ) {
        updatedMessages[userIndex] = {
          ...updatedMessages[userIndex],
          id: assistantMessageId,
        };
      }

      localStorage.setItem(
        `messages_${threadId}`,
        JSON.stringify(updatedMessages),
      );
      return updatedMessages;
    });
  };

  const updateLatestMessageSources = (threadId, sources) => {
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const assistantIndex = findLastAssistantMessageIndex(updatedMessages);

      if (assistantIndex === -1) return prevMessages;

      updatedMessages[assistantIndex] = {
        ...updatedMessages[assistantIndex],
        sources,
      };

      localStorage.setItem(
        `messages_${threadId}`,
        JSON.stringify(updatedMessages),
      );
      return updatedMessages;
    });
  };

  const readResponsesApiStream = async (response, threadId) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const accumulatedTextRef = { value: "" };
    let buffer = "";

    const processEvent = (rawEvent) => {
      const lines = rawEvent.split(/\r?\n/);
      const eventType = (
        lines
          .find((line) => line.startsWith("event:"))
          ?.replace(/^event:\s?/, "") || "message"
      ).trim();
      const data = lines
        .filter((line) => line.startsWith("data:"))
        .map((line) =>
          line.startsWith("data: ") ? line.slice(6) : line.slice(5),
        )
        .join("\n");

      if (!data) return;

      if (eventType === "message_id") {
        const assistantMessageId = Number(data);
        if (!Number.isNaN(assistantMessageId)) {
          updateLatestMessageIds(threadId, assistantMessageId);
        }
        return;
      }

      if (eventType === "sources") {
        try {
          const parsed = JSON.parse(data);
          updateLatestMessageSources(threadId, parsed?.sources || []);
        } catch (error) {
          console.error("Error parsing sources event:", error);
        }
        return;
      }

      const errorPayload = parseStreamErrorPayload(data);
      if (errorPayload) {
        throw createStreamError(
          getErrorText(errorPayload),
          isTokenLimitError(null, errorPayload) ? "token_limit" : "error",
        );
      }

      appendStreamText(threadId, data, accumulatedTextRef);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";
      events.forEach(processEvent);
    }

    buffer += decoder.decode();
    if (buffer.trim()) processEvent(buffer);
  };

  const handleResponsesApiMessage = async (
    threadId,
    formData,
    trimmedMessage,
    tokenCount,
  ) => {
    const controller = new AbortController();
    eventSourceRef.current = { close: () => controller.abort() };

    const response = await fetch(
      `https://${baseURL}/v2/responses-chat/conversation/${threadId}/message`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      const errorPayload = await readErrorBody(response);
      if (isTokenLimitError(response, errorPayload)) {
        handleStreamFailure(threadId, "responses_api", "token_limit");
        return;
      }
      throw createStreamError(
        getErrorText(errorPayload),
        isTokenLimitError(response, errorPayload) ? "token_limit" : "error",
      );
    }

    if (!response.body) {
      handleStreamFailure(threadId, "responses_api", "empty");
      return;
    }

    await updatePromptTokenCount(tokenCount);

    try {
      await readResponsesApiStream(response, threadId);
      await handleRename(token, threadId, trimmedMessage, setRefresh, botRole);
      refreshThreadHistoryOrder(threadId);
    } catch (error) {
      handleStreamFailure(
        threadId,
        "responses_api",
        error.streamFailureReason || "error",
      );
    } finally {
      setIsLoading(false);
      setWait(false);
      eventSourceRef.current = null;
    }
  };
  const updateMessageState = (threadId, cleanedText) => {
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const assistantIndex = findLastAssistantMessageIndex(updatedMessages);

      if (assistantIndex === -1) {
        updatedMessages.push({
          role: "assistant",
          text_value: cleanedText,
          isAnalyzing: false,
          timestamp: new Date().toISOString(),
        });
      } else {
        updatedMessages[assistantIndex] = {
          ...updatedMessages[assistantIndex],
          role: "assistant",
          text_value: cleanedText,
          isAnalyzing: false,
        };
      }

      localStorage.setItem(
        `messages_${threadId}`,
        JSON.stringify(updatedMessages),
      );
      return updatedMessages;
    });
  };

  return { handleSendMessage };
};

export const handleGenerateName = async (message, token) => {
  try {
    const response = await api.post(
      `/chat/generate/name?query=${encodeURIComponent(message)}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data.short_name;
  } catch (error) {
    console.error("Failed to rename thread:", error);
  }
};
export const handleRename = async (
  token,
  threadId,
  message,
  setRefresh,
  botRole,
) => {
  try {
    const sidebarHistory = JSON.parse(
      localStorage.getItem("sidebar_history") || "{}",
    );

    const allThreads = Object.values(sidebarHistory.data || {}).flat();
    const currentThread = allThreads.find(
      (thread) => thread.thread_id === threadId,
    );

    // Ensure the thread has not already been renamed
    if (
      currentThread &&
      currentThread.name !== "New Thread" &&
      currentThread.name !== "EC Thread" &&
      currentThread.name !== "Invoicing Thread" &&
      currentThread.name !== "New Chat"
    ) {
      return;
    }

    // Generate a new name for the thread
    const generatedName = await handleGenerateName(message, token);
    const newName = generatedName;

    // Check if the generated name is the same as the existing name
    if (currentThread && currentThread.name === newName) {
      return; // Avoid redundant renaming
    }

    await api.patch(
      `/v2/responses-chat/conversation/${threadId}/title`,
      { title: newName },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    // Update local storage immediately
    if (sidebarHistory.data) {
      for (const section in sidebarHistory.data) {
        sidebarHistory.data[section] = sidebarHistory.data[section].map(
          (thread) =>
            thread.thread_id === threadId
              ? { ...thread, name: newName } // Update thread name
              : thread,
        );
      }
      localStorage.setItem("sidebar_history", JSON.stringify(sidebarHistory));
    }

    // Trigger a refresh to update the UI
    setRefresh((prev) => !prev);
  } catch (error) {
    console.error("Failed to rename thread:", error);
  }
};

export function preprocessLaTeX(content) {
  // Step 1: Protect code blocks
  const codeBlocks = [];
  content = content.replace(/(```[\s\S]*?```|`[^`\n]+`)/g, (match) => {
    codeBlocks.push(match);
    return `<<CODE_BLOCK_${codeBlocks.length - 1}>>`;
  });

  // Step 2: Protect existing LaTeX expressions
  const latexExpressions = [];
  content = content.replace(
    /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\\\(.*?\\\))/g,
    (match) => {
      latexExpressions.push(match);
      return `<<LATEX_${latexExpressions.length - 1}>>`;
    },
  );

  // Step 3: Escape dollar signs that are likely currency indicators
  content = content.replace(/\$(?=\d)/g, "\\$");

  // Step 4: Replace newlines with double newlines for markdown
  content = content.replace(/\n/g, "\n\n");

  // Step 5: Restore LaTeX expressions
  content = content.replace(
    /<<LATEX_(\d+)>>/g,
    (_, index) => latexExpressions[parseInt(index)],
  );

  // Step 6: Restore code blocks
  content = content.replace(
    /<<CODE_BLOCK_(\d+)>>/g,
    (_, index) => codeBlocks[parseInt(index)],
  );

  // Step 7: Apply additional escaping functions
  content = escapeBrackets(content);
  content = escapeMhchem(content);

  return content;
}

export function escapeBrackets(text) {
  const pattern =
    /(```[\S\s]*?```|`.*?`)|\\\[([\S\s]*?[^\\])\\]|\\\((.*?)\\\)/g;
  return text.replace(
    pattern,
    (match, codeBlock, squareBracket, roundBracket) => {
      if (codeBlock != null) {
        return codeBlock;
      } else if (squareBracket != null) {
        return `$$${squareBracket}$$`;
      } else if (roundBracket != null) {
        return `$${roundBracket}$`;
      }
      return match;
    },
  );
}

export function escapeMhchem(text) {
  return text.replaceAll("$\\ce{", "$\\\\ce{").replaceAll("$\\pu{", "$\\\\pu{");
}
